import React, { FC, useEffect, useRef, useState } from 'react';
// Types
import { ContactType, TargetType } from 'api/graphql-global-types';
// Ui
import { Mention } from 'ui3/Mentions/Mentions';
import Button from 'ui3/Button/Button';
import Text from 'ui3/Text/Text';
// Components
import UnregisteredMentionsSelect from '../UnregisteredMentionsSelect/UnregisteredMentionsSelect';
// Styles
import styles from './OutsideMillionsBrandsSelect.module.scss';

interface OutsideMillionsSelectProps {
  mentions: Mention[];
  onChange: (values: Mention[]) => void;
}

type MentionWithFlag = Mention & { isAdded?: boolean };

export const OutsideMillionsBrandsSelect: FC<OutsideMillionsSelectProps> = ({
  mentions,
  onChange,
}) => {
  const [unregisteredBrandsMentions, setUnregisteredBrandsMentions] = useState<
    MentionWithFlag[]
  >(
    mentions.map((m) => ({
      ...m,
      isAdded: m?.id ? true : (m as any)?.isAdded || false,
    }))
  );
  const previousMentionsRef = useRef(unregisteredBrandsMentions);
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Only sync from props on initial mount or when mentions are cleared
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (mentions && mentions.length > 0) {
        setUnregisteredBrandsMentions(
          mentions.map((m) => ({
            ...m,
            isAdded: m?.id ? true : (m as any)?.isAdded || false,
          }))
        );
      }
    } else if (
      mentions &&
      mentions.length === 0 &&
      unregisteredBrandsMentions.length === 0
    ) {
      // Reset when mentions are cleared
      setUnregisteredBrandsMentions([]);
    }
  }, [mentions, unregisteredBrandsMentions.length]);

  const handleUnregisteredBrandsChange = (mention, index) => {
    setUnregisteredBrandsMentions((prevMentions) => {
      const updatedMentions = [...prevMentions];
      const previousMention = updatedMentions[index];
      // Preserve the isAdded flag when updating
      updatedMentions[index] = {
        ...mention,
        isAdded: previousMention?.isAdded || false,
      };
      return updatedMentions;
    });
  };

  const isMentionCompleted = (mention: MentionWithFlag): boolean => {
    // If it has an id, it's definitely completed (saved to backend)
    if (mention?.id) {
      return true;
    }
    // Otherwise check the isAdded flag
    return mention?.isAdded || false;
  };

  useEffect(() => {
    // Compare the previous and current mentions arrays
    if (
      JSON.stringify(previousMentionsRef.current) !==
      JSON.stringify(unregisteredBrandsMentions)
    ) {
      // Strip isAdded flag before passing to parent (parent expects Mention[])
      const mentionsForParent = unregisteredBrandsMentions.map((mention) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { isAdded, ...mentionWithoutFlag } = mention;
        return mentionWithoutFlag;
      });
      onChange(mentionsForParent);
      previousMentionsRef.current = unregisteredBrandsMentions; // Update the ref with the current mentions
    }
  }, [unregisteredBrandsMentions, onChange]);

  const addNewBrandsMention = () => {
    setUnregisteredBrandsMentions((prevMentions) => [
      ...prevMentions,
      {
        id: null,
        targetType: TargetType.UNREGISTERED,
        user: null,
        unregisteredContact: {
          name: '',
          email: '',
          socialMediaLink: '',
          phoneNumberE164: '',
          type: ContactType.BRAND,
        } as any,
        isAdded: false,
      },
    ]);
  };

  const handleOtherAthleteMentionRemove = (index: number) => {
    setUnregisteredBrandsMentions((prevMentions) => {
      const updatedMentions = [...prevMentions];
      updatedMentions.splice(index, 1);
      return updatedMentions;
    });
  };

  const handleComplete = (index: number) => {
    setUnregisteredBrandsMentions((prevMentions) => {
      const updatedMentions = [...prevMentions];
      updatedMentions[index] = {
        ...updatedMentions[index],
        isAdded: true,
      };
      return updatedMentions;
    });
  };

  return (
    <div className={styles.root}>
      <Text variant="subtitle2">Brands/Organizations outside of MILLIONS</Text>

      <Text variant="body2Regular" color="lights-low">
        Mention brands by using their names and links to directly engage them
        and increase interaction on your content.
      </Text>

      <div className={styles.athletesSelectContainer}>
        {/* Render all mentions with their actual indices */}
        {unregisteredBrandsMentions.map((mention, index) => {
          const isCompleted = isMentionCompleted(mention);
          const mentionKey =
            mention.id ||
            mention?.unregisteredContact?.name ||
            (mention as any)?.name ||
            `mention-${index}`;

          return (
            <div
              key={
                isCompleted
                  ? `completed-${mentionKey}-${index}`
                  : `in-progress-${mentionKey}-${index}`
              }
              className={styles.selectContainer}
            >
              <UnregisteredMentionsSelect
                mention={mention}
                index={index}
                type={ContactType.BRAND}
                onChange={handleUnregisteredBrandsChange}
                handleMillionsMentionRemove={handleOtherAthleteMentionRemove}
                isCompleted={isCompleted}
                onComplete={isCompleted ? undefined : handleComplete}
              />
            </div>
          );
        })}

        <div>
          <Button
            type="button"
            variant="secondary"
            subVariant="outlined"
            icon="plus"
            onClick={addNewBrandsMention}
          >
            New Mention
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OutsideMillionsBrandsSelect;
